import { getTableContainerWidth } from '@atlaskit/editor-common/node-width';
import type { Node } from '@atlaskit/editor-prosemirror/model';
import {
	akEditorDefaultLayoutWidth,
	akEditorFullWidthLayoutWidth,
	akEditorMaxWidthLayoutWidth,
} from '@atlaskit/editor-shared-styles/consts';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/editor-experiment';

// Platform's renderer padding (`FullPagePadding` in @atlaskit/renderer's Renderer styles).
const FULL_PAGE_PADDING = 32;

export type TableContainerStyleProps = {
	allowTableAlignment?: boolean;
	allowTableResizing?: boolean;
	isInsideOfBlockNode?: boolean;
	isInsideOfTable?: boolean;
	rendererAppearance?: string;
	tableNode?: Node;
};

/**
 * The container width and breakout offset of Platform's `TableContainer.render`
 * (`platform/packages/editor/renderer/src/react/nodes/table.tsx`) for the appearances light
 * rendering runs in. `tableContainerStyle-test` compares the two.
 */
export function getTableContainerStyle({
	allowTableAlignment,
	allowTableResizing,
	isInsideOfBlockNode,
	isInsideOfTable,
	rendererAppearance = 'full-page',
	tableNode,
}: TableContainerStyleProps): { width: string; left?: string; marginLeft?: string } {
	const fullPageRendererWidthCSS = editorExperiment(
		'platform_editor_preview_panel_responsiveness',
		true,
		{ exposure: true },
	)
		? 'calc(100cqw - var(--ak-renderer--full-page-gutter) * 2)'
		: `100cqw - ${FULL_PAGE_PADDING}px * 2`;
	const renderWidthCSS = rendererAppearance === 'full-page' ? fullPageRendererWidthCSS : '100cqw';
	const hasCustomWidth = Boolean(tableNode?.attrs.width);
	const tableWidthCSS =
		rendererAppearance === 'max' && !hasCustomWidth
			? `min(${akEditorMaxWidthLayoutWidth}px, ${renderWidthCSS})`
			: rendererAppearance === 'full-width' && !hasCustomWidth
				? `min(${akEditorFullWidthLayoutWidth}px, ${renderWidthCSS})`
				: `min(${getTableContainerWidth(tableNode)}px, ${renderWidthCSS})`;
	const lineLengthCSS =
		rendererAppearance === 'full-width'
			? `min(${akEditorFullWidthLayoutWidth}px, ${renderWidthCSS})`
			: rendererAppearance === 'max'
				? `min(${akEditorMaxWidthLayoutWidth}px, ${renderWidthCSS})`
				: `${akEditorDefaultLayoutWidth}px`;
	const isAlignStart = tableNode?.attrs.layout === 'align-start' && Boolean(allowTableAlignment);
	const alignsLeft =
		!isInsideOfBlockNode &&
		!isInsideOfTable &&
		isAlignStart &&
		((rendererAppearance === 'full-page' &&
			getTableContainerWidth(tableNode) <= akEditorDefaultLayoutWidth) ||
			rendererAppearance === 'full-width' ||
			rendererAppearance === 'max');
	let left: string | undefined;
	if (alignsLeft) {
		left = `(${tableWidthCSS} - ${lineLengthCSS}) / 2`;
	} else if (rendererAppearance === 'full-page') {
		// Wide tables break out of the content column; narrow screens keep them left-aligned.
		left = `min(0px, ${lineLengthCSS} - ${tableWidthCSS}) / 2`;
	}
	return {
		width: allowTableResizing ? `calc(${tableWidthCSS})` : 'inherit',
		left: left ? `calc(${left})` : undefined,
		marginLeft: alignsLeft && left ? `calc(-1 * (${left}))` : undefined,
	};
}
